import { useCallback, useMemo } from 'react';

import { OperationVariables, QueryResult, useLazyQuery } from '@apollo/client';
import { useGraphQLQuery } from '@betterment/graphql';

import { Employer, LoadingStatus } from './data';
import { GetPlanManagerSidebarDocument } from './graphql/query.getPlanManagerSidebar.generated';
import { GetPlanManagerSidebarEmployersWithTasksDocument } from './graphql/query.getPlanManagerSidebarEmployersWithTasks.generated';
import { createTaskCountsFromEmployers } from './helpers';

const getLoadingStatus = <T, U extends OperationVariables>(queryResult: QueryResult<T, U>): LoadingStatus => {
  if (queryResult.error) return 'error';
  if (queryResult.data !== undefined) return 'loaded';

  // Initial request is still loading
  return 'loading';
};

// For lazy queries, we need to account for the fact that they may not have been called yet
const getLazyLoadingStatus = <T, U extends OperationVariables>(queryResult: QueryResult<T, U>): LoadingStatus => {
  if (queryResult.error) return 'error';
  if (queryResult.loading) return 'loading';
  if (queryResult.data !== undefined) return 'loaded';
  return 'idle'; // Not yet fetched
};

export const useSidebarDataQuery = (employerId: string) => {
  const query = useGraphQLQuery(GetPlanManagerSidebarDocument, {
    variables: { employerId },
  });

  const { data, refetch } = query;

  const loadingStatus: LoadingStatus = getLoadingStatus(query);
  const employers: Employer[] = data?.employers.filter(Boolean) || [];
  const currentEmployer = data?.employer;
  const personalProfile = data?.personalProfile;

  const reload = useCallback(() => {
    void refetch();
  }, [refetch]);

  return {
    employers,
    currentEmployer,
    personalProfile,
    loadingStatus,
    reload,
  };
};

export const useEmployersWithTasksQuery = () => {
  const [loader, query] = useLazyQuery(GetPlanManagerSidebarEmployersWithTasksDocument);

  const { data } = query;

  const tasksLoadingStatus: LoadingStatus = getLazyLoadingStatus(query);

  const taskCounts: Record<string, number> = useMemo(
    () => createTaskCountsFromEmployers(data?.employers || []),
    [data]
  );

  const loadEmployersWithTasks = useCallback(() => {
    void loader();
  }, [loader]);

  return {
    taskCounts,
    tasksLoadingStatus,
    loadEmployersWithTasks,
  };
};
